import React, { useState, useEffect } from 'react';
import { Upload, FileText, X, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api, { studentsAPI, staffAPI, consultantsAPI, masterSettingsAPI } from '@/api/api';
import { useNavigate } from 'react-router-dom';
import { readFileSmart } from '@/utils/imageCompressor';

const UploadMarksheet = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    entity_id: '',
    entity_name: '',
    roll_no: '',
    title: '',
    session: '',
    course: '',
    semester: '',
    year: '',
    file_name: '',
    file_data: '',
    file_type: '',
    file_size: 0,
    description: ''
  });

  useEffect(() => {
    fetchStudents();
    fetchMasterData();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get('/students');
      setStudents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchMasterData = async () => {
    try {
      const [sessionsRes, coursesRes, semestersRes] = await Promise.all([
        api.get('/master-settings/sessions'),
        api.get('/master-settings/courses'),
        api.get('/master-settings/semesters')
      ]);
      setSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
      setCourses(Array.isArray(coursesRes.data) ? coursesRes.data : []);
      setSemesters(Array.isArray(semestersRes.data) ? semestersRes.data : []);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const processSelectedFile = async (file, originalName, originalSize, mimeType) => {
    if (originalSize > 5 * 1024 * 1024) { alert('File size must be less than 5MB'); return; }
    try {
      const result = await readFileSmart(file);
      setFormData(prev => ({
        ...prev,
        file_name: originalName,
        file_data: result.data,
        file_type: result.type || (mimeType.split('/')[1] || 'pdf'),
        file_size: result.size
      }));
      if (result.compressed) {
        console.log(`Image compressed: ${(originalSize/1024).toFixed(0)}KB -> ${(result.size/1024).toFixed(0)}KB`);
      }
    } catch (err) {
      console.error('File read failed:', err);
      alert('Failed to read file. Please try again.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    processSelectedFile(file, file.name, file.size, file.type || 'application/octet-stream');
  };

  const handleStudentSelect = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (student) {
      setFormData({
        ...formData,
        entity_id: student.id,
        entity_name: student.full_name,
        roll_no: student.roll_no || '',
        course: student.course || '',
        semester: student.semester || ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file_data) {
      alert('Please select a file to upload');
      return;
    }
    if (!formData.entity_id) {
      alert('Please select a student');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/transcripts/documents', {
        ...formData,
        entity_type: 'student',
        document_type: 'marksheet',
        title: formData.title || `Marksheet - ${formData.semester} - ${formData.session}`
      });
      alert('Marksheet uploaded successfully!');
      navigate('/transcripts/marksheet');
    } catch (error) {
      alert('Failed to upload marksheet');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => navigate('/transcripts/marksheet')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Marksheet</h1>
          <p className="text-sm text-gray-500 mt-1">Upload student marksheet document</p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Select Student *</Label>
                <Select value={formData.entity_id} onValueChange={handleStudentSelect}>
                  <SelectTrigger><SelectValue placeholder="Select a student" /></SelectTrigger>
                  <SelectContent>
                    {students.map(student => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.full_name} - {student.roll_no}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Roll No</Label>
                <Input value={formData.roll_no} readOnly className="bg-gray-50" />
              </div>

              <div>
                <Label>Session</Label>
                <Select value={formData.session} onValueChange={(v) => setFormData({...formData, session: v})}>
                  <SelectTrigger><SelectValue placeholder="Select session" /></SelectTrigger>
                  <SelectContent>
                    {sessions.map(session => (
                      <SelectItem key={session.id} value={session.name}>{session.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Course</Label>
                <Select value={formData.course} onValueChange={(v) => setFormData({...formData, course: v})}>
                  <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                  <SelectContent>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.name}>{course.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Semester</Label>
                <Select value={formData.semester} onValueChange={(v) => setFormData({...formData, semester: v})}>
                  <SelectTrigger><SelectValue placeholder="Select semester" /></SelectTrigger>
                  <SelectContent>
                    {semesters.map(sem => (
                      <SelectItem key={sem.id} value={sem.name}>{sem.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Year</Label>
                <Input 
                  value={formData.year} 
                  onChange={(e) => setFormData({...formData, year: e.target.value})}
                  placeholder="e.g., 2024"
                />
              </div>

              <div>
                <Label>Document Title</Label>
                <Input 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., Semester 1 Marksheet"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Input 
                type="file" 
                accept=".pdf,.jpg,.jpeg,.png" 
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600 font-medium">Click to upload marksheet</p>
                <p className="text-sm text-gray-500 mt-1">PDF, JPG, PNG (max 5MB)</p>
              </label>
              {formData.file_name && (
                <div className="mt-4 flex items-center justify-center gap-2 text-green-600">
                  <FileText className="h-5 w-5" />
                  <span>{formData.file_name}</span>
                  <button type="button" onClick={() => setFormData({...formData, file_name: '', file_data: '', file_type: '', file_size: 0})}>
                    <X className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/transcripts/marksheet')} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? 'Uploading...' : 'Upload Marksheet'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UploadMarksheet;
